import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RawData_URL, SURVEY_URL } from '@insite-store360/shared';
import { BehaviorSubject, catchError, Observable } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class JourneyService {
  private selectedRowDataSource = new BehaviorSubject<any>(
    this.getSessionData()
  );
  selectedRowData$ = this.selectedRowDataSource.asObservable();
  private readonly JOURNEY_API = `${SURVEY_URL}`;
  private readonly RAWDATA_URL = `${RawData_URL}`
  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  setSelectedRow(data: any): void {
    this.selectedRowDataSource.next(data);
    sessionStorage.setItem('selectedRowData', JSON.stringify(data));
  }

  getSelectedRow() {
    return this.selectedRowDataSource.getValue();
  }

  clearSelectedRow(): void {
    this.selectedRowDataSource.next(null);
    sessionStorage.removeItem('selectedRowData');
  }

  private getSessionData() {
    const data = sessionStorage.getItem('selectedRowData');
    return data ? JSON.parse(data) : null;
  }

  public createJourneyPlan(params: {
    id: number | undefined,
    // surveyMapId: number;
    date: string,
    storeId: number,
    userId: number
  }): Observable<any> {
    const url = `${this.JOURNEY_API}/JourneyPlan/save`;
    return this.http
      .post<any>(url, params)
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getAllUsersForJourneyPlan(
    pageIndex: Number,
    pageSize: Number,
    searchFilter?: string,
    userId?: Number
  ): Observable<any> {
    if (searchFilter) {
      return this.http
        .get<any>(
          `${this.JOURNEY_API}/JourneyPlan/getAllUsersForJourneyPlan?id=${userId}&pageIndex=${pageIndex}&pageSize=${pageSize}&searchFilter=${searchFilter}`
        )
        .pipe(catchError(this.errorHandler.handleError));
    } else {
      return this.http
        .get<any>(
          `${this.JOURNEY_API}/JourneyPlan/getAllUsersForJourneyPlan?id=${userId}&pageIndex=${pageIndex}&pageSize=${pageSize}`
        )
        .pipe(catchError(this.errorHandler.handleError));
    }
  }
  public getStoreBasedOnUserAndCountryId(

    userId: number,
    countryId: number,
    storeId?: number,
    roleId?: string,
    stateId?: number,
    searchFilter?: string,
    pageIndex?: number,
    pageSize?: number,
  ): Observable<any> {
    let queryParams = new HttpParams();

    if (pageSize) {
      queryParams = queryParams.set('pageSize', pageSize);
    }

    if (pageIndex) {
      queryParams = queryParams.set('pageIndex', pageIndex);
    }

    if (searchFilter) {
      queryParams = queryParams.set('searchFilter', searchFilter);
    }

    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }

    if (userId) {
      queryParams = queryParams.set('userId', userId);
    }

    if (roleId) {
      queryParams = queryParams.set('roleId', roleId);
    }

    if (storeId) {
      queryParams = queryParams.set('storeId', storeId);
    }
    if (stateId) {
      queryParams = queryParams.set('stateId', stateId);
    }


    return this.http
      .get<any>(
        `${this.JOURNEY_API}/JourneyPlan/getStoreBasedOnUserAndCountryId`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllJourneyPlanByUserAndStoreId(
    userId: number,
    roleId: number,
    countryId: number,
    stateId: number,
    year: number,
    month: number,
    // surveyMapId: number
  ): Observable<any> {
    const url = `${this.JOURNEY_API}/JourneyPlan/getAllJourneyPlanByUserAndStoreId?userId=${userId}&roleId=${roleId}&countryId=${countryId}&stateId=${stateId}&year=${year}&month=${month}`;
    return this.http
      .get<any>(url)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getJpCalenderUsers(
    month: number,
    year: number,
    countryId?: number,
    stateId?: any,
    roleId?: string | number,
    searchFilter?: string,
    pageIndex?: number,
    pageSize?: number,
    promoterCategoryId?: number,
    userId?: number,
    accountId?: string | number,
    storeId?: string | number
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('month', month)
      .set('year', year);

    if (countryId) queryParams = queryParams.set('countryId', countryId);
    if (stateId) queryParams = queryParams.set('stateId', stateId);
    if (roleId) queryParams = queryParams.set('roleId', roleId);
    if (searchFilter) queryParams = queryParams.set('searchFilter', searchFilter);
    if (pageIndex) queryParams = queryParams.set('pageIndex', pageIndex);
    if (pageSize) queryParams = queryParams.set('pageSize', pageSize);
    if (promoterCategoryId) queryParams = queryParams.set('promoterCategoryId', promoterCategoryId);
    if (userId) queryParams = queryParams.set('userId', userId);
    if (accountId) queryParams = queryParams.set('accountId', accountId);
    if (storeId) queryParams = queryParams.set('storeId', storeId);

    return this.http
      .get<any>(`${this.JOURNEY_API}/JourneyPlan/getJpCalenderUsers`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadExcelTemplate(selectedMonthYear: string, countryId: number, roleId: number, surveyMapId: number) {
    return this.http
      .get(`${this.JOURNEY_API}/JourneyPlan/downloadExcelTemplate`, {
        params: { selectedMonthYear, countryId, roleId, surveyMapId },
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }
  public importJourney(data: Record<string, any>): Observable<any[]> {
    return this.http
      .post<any[]>(`${this.JOURNEY_API}/JourneyPlan/importData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public downloadJourneyPlanTemplate(): Observable<Blob> {
    return this.http
      .get(`${this.JOURNEY_API}/JourneyPlan/downloadExcelTemplate`, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }


  /**
 * Imports a list of store into the server.
 * @param data - An array of store objects to be imported.
 * @returns An Observable that emits an array of imported journey plan objects upon successful import.
 */
  public importJourneyPlan(data: Record<string, any>): Observable<any[]> {
    return this.http
      .post<any[]>(`${this.JOURNEY_API}/JourneyPlan/importData`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public importJourneyPlanExcel(payload: {
    month: number;
    year: number;
    data: Array<{
      date: string;
      storeCode: string;
      storeName: string;
      userName: string;
      name: string;
    }>;
  }): Observable<any> {
    return this.http
      .post<any>(`${this.JOURNEY_API}/JourneyPlan/importExcel`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }



  public toggleCalenderCell(payload: { userId: number; storeId: number; date: string; status: number }): Observable<any> {
    return this.http
      .post<any>(`${this.JOURNEY_API}/JourneyPlan/toggleCalenderCell`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public saveBulkForCalender(payload: {
    month: number;
    year: number;
    data: Array<{
      date: string;
      storeCode: string;
      storeName: string;
      userName: string;
      name: string;
    }>;
  }): Observable<any> {
    return this.http
      .post<any>(`${this.RAWDATA_URL}/rawJourneyPlan/saveBulkForCalender`, payload)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllAttendancePromotersExcelReport(

    params: { pageIndex?: number, pageSize?: number, startDate?: string, endDate?: string, searchFilter?: string, status: number, countryId: number }): Observable<Blob> {


    const columns: string[] = [
      'Country',
      'PromoterName',
      'Email',
      'Gender',
      'Role',
      'Status'
    ];
    return this.http
      .post<Blob>(`${this.JOURNEY_API}/JourneyPlan/getAllNoAttendancePromoters/downloadExcelReport`, { columns, ...params }, { responseType: 'blob' as 'json' })
      .pipe(catchError(this.errorHandler.handleError));
  }
  public getAllNoAttendancePromoters(params: { pageIndex?: number, pageSize?: number, startDate?: string, endDate?: string, searchFilter?: string, status: number, countryId: number }): Observable<any> {

    let queryParams = new HttpParams();

    if (params.pageSize) {
      queryParams = queryParams.set('pageSize', params.pageSize);
    }

    if (params.pageIndex) {
      queryParams = queryParams.set('pageIndex', params.pageIndex);
    }

    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryId) {

      queryParams = queryParams.set('countryId', params.countryId);
    }

    if (params.status !== null && params.status !== undefined) {
      queryParams = queryParams.set('status', params.status);
    }
    if (params.startDate) {
      queryParams = queryParams.set('startDate', params.startDate);
    }
    if (params.endDate) {
      queryParams = queryParams.set('endDate', params.endDate);
    }

    return this.http
      .get(`${this.JOURNEY_API}/JourneyPlan/getAllNoAttendancePromoters`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getAllJourneyPlanDashboard(params: {
    userId?: number;
    pageIndex: number;
    pageSize: number;
    countryId?: number;
    stateId?: number;
    roleId?: number;
    search?: string;
    promoterCategoryId?: number;
    month: number;
    year: number;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('month', params.month)
      .set('year', params.year)
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.userId) queryParams = queryParams.set('userId', params.userId);
    if (params.countryId) queryParams = queryParams.set('countryId', params.countryId);
    if (params.stateId) queryParams = queryParams.set('stateId', params.stateId);
    if (params.roleId) queryParams = queryParams.set('roleId', params.roleId);
    if (params.search) queryParams = queryParams.set('search', params.search);
    if (params.promoterCategoryId) queryParams = queryParams.set('promoterCategoryId', params.promoterCategoryId);

    return this.http
      .get<any>(`${this.JOURNEY_API}/JourneyPlan/getAllJourneyPlan`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getDashboardData(params: {
    month: string | number;
    year: number;
    countryId?: string | number;
    roleId?: string | number;
    stateId?: string | number;
    cityId?: string | number;
    areaGroupId?: string | number;
    regionId?: string | number;
    tdsUserIds?: string | number;
    tdlUserIds?: string | number;
    accountId?: string | number;
    storeGrade?: string;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('month', params.month)
      .set('year', params.year);

    if (params.countryId) queryParams = queryParams.set('countryId', params.countryId);
    if (params.roleId) queryParams = queryParams.set('roleId', params.roleId);
    if (params.stateId) queryParams = queryParams.set('stateId', params.stateId);
    if (params.cityId) queryParams = queryParams.set('cityId', params.cityId);
    if (params.areaGroupId) queryParams = queryParams.set('areaGroupId', params.areaGroupId);
    if (params.regionId) queryParams = queryParams.set('regionId', params.regionId);
    if (params.tdsUserIds) queryParams = queryParams.set('tdsUserIds', params.tdsUserIds);
    if (params.tdlUserIds) queryParams = queryParams.set('tdlUserIds', params.tdlUserIds);
    if (params.accountId) queryParams = queryParams.set('accountId', params.accountId);
    if (params.storeGrade) queryParams = queryParams.set('storeGrade', params.storeGrade);

    return this.http
      .get<any>(`${this.JOURNEY_API}/JourneyPlan/getDashboardData`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

}
